import { uniq } from "lodash";
/** Names this part of the interface in the user's stored settings. */
export const HOME_LAYOUT_SCOPE = "home.layout";

/** A folder on the home page. Its position in the list is its order. */
export interface HomeFolder {
  id: string;
  name: string;
  /** The pages inside, by the file name each add-in page is published under. */
  pageUrls: string[];
}

/**
 * How one reader has arranged their home page.
 *
 * A type rather than an interface because the settings store takes anything indexable by string.
 */
export type HomeLayoutSettings = {
  folders: HomeFolder[];
  /** Starred pages, in the order they were starred, shown above the folders. */
  favoriteUrls: string[];
};

export const HOME_LAYOUT_DEFAULTS: HomeLayoutSettings = {
  folders: [],
  favoriteUrls: [],
};

/** Where a page goes before anybody has arranged anything. */
interface FolderBlueprint {
  id: string;
  /** Not translated: a folder is a thing the reader renames, and a rename cannot be translated. */
  name: string;
  pageUrls: string[];
}

/**
 * The folders a reader starts with.
 *
 * Only a starting point. Everything here can be renamed, reordered, emptied or deleted, and once
 * a reader has touched it their own arrangement is what gets stored — the blueprint is never
 * applied again.
 */
const FOLDER_BLUEPRINTS: FolderBlueprint[] = [
  {
    id: "monitoring",
    name: "Monitoring",
    pageUrls: [
      "fleet-dashboard.html",
      "diagnosticmonitor.html",
      "statusmonitor.html",
      "faultmonitor.html",
      "fuelcell.html",
      "speed-limit-report.html",
      "tachograph.html",
      "vehicle-data-check.html",
      "energy-consumption-report.html",
    ],
  },
  {
    id: "operations",
    name: "Betrieb",
    pageUrls: [
      "maintenance.html",
      "route.html",
      "map.html",
      "assets.html",
      "logbook-edit.html",
      "motor-data.html",
      "live-data.html",
    ],
  },
  {
    id: "setup",
    name: "Einrichtung",
    pageUrls: [
      "diagnostic-edit.html",
      "pmrules-edit.html",
      "device-firmware.html",
      "presets.html",
      "ibis-id.html",
      "license-check-admin.html",
    ],
  },
  {
    id: "admin",
    name: "Administration",
    pageUrls: ["admin.html"],
  },
];

/** Holds whatever the blueprint did not think of, so no page is unreachable. */
const CATCH_ALL_FOLDER = { id: "other", name: "Weitere" };
/** Used only if a reader manages to delete their way down to nothing. */
const EMPTY_FOLDER = { id: "general", name: "Allgemein" };

/**
 * The starting folders, for the pages this installation actually has.
 *
 * A page the blueprint does not mention — a new one, or one this customer is not licensed for —
 * still has to be reachable, so anything left over is collected at the end rather than dropped.
 *
 * @param knownPageUrls - The pages that exist, in the order the registry lists them.
 */
export function buildDefaultFolders(knownPageUrls: readonly string[]): HomeFolder[] {
  const known = new Set(knownPageUrls);
  const placed = new Set<string>();

  const folders = FOLDER_BLUEPRINTS.flatMap(blueprint => {
    const pageUrls = blueprint.pageUrls.filter(url => known.has(url));
    pageUrls.forEach(url => placed.add(url));
    return pageUrls.length > 0 ? [{ id: blueprint.id, name: blueprint.name, pageUrls }] : [];
  });

  const leftovers = knownPageUrls.filter(url => !placed.has(url));
  if (leftovers.length > 0) {
    folders.push({ ...CATCH_ALL_FOLDER, pageUrls: [...leftovers] });
  }

  return folders.length > 0 ? folders : [{ ...EMPTY_FOLDER, pageUrls: [] }];
}

/**
 * Brings a stored arrangement back in line with the pages that exist.
 *
 * Stored settings outlive the code around them. Pages come and go, a customer's licence changes,
 * and a record written on another day can hold a page twice or a page that is gone. What must
 * never happen is a page nobody can reach, so anything missing is added at the end.
 *
 * @param folders - What was stored.
 * @param knownPageUrls - The pages that exist.
 */
export function reconcileFolders(
  folders: readonly HomeFolder[],
  knownPageUrls: readonly string[],
): HomeFolder[] {
  const known = new Set(knownPageUrls);
  const seen = new Set<string>();

  const cleaned = folders.flatMap(folder => {
    if (!folder?.id) {
      return [];
    }

    // First occurrence wins: a page lives in one folder, and a record that says otherwise has to
    // be resolved one way or the other rather than showing the page twice.
    const pageUrls = (folder.pageUrls ?? []).filter(url => {
      if (!known.has(url) || seen.has(url)) {
        return false;
      }
      seen.add(url);
      return true;
    });

    return [{ id: folder.id, name: folder.name || folder.id, pageUrls }];
  });

  const missing = knownPageUrls.filter(url => !seen.has(url));
  if (missing.length === 0) {
    return cleaned.length > 0 ? cleaned : [{ ...EMPTY_FOLDER, pageUrls: [] }];
  }

  const catchAll = cleaned.find(folder => folder.id === CATCH_ALL_FOLDER.id);
  if (catchAll) {
    catchAll.pageUrls.push(...missing);
    return cleaned;
  }

  return [...cleaned, { ...CATCH_ALL_FOLDER, pageUrls: [...missing] }];
}

/** Drops starred pages that no longer exist, and any duplicates. */
export function reconcileFavorites(
  favoriteUrls: readonly string[],
  knownPageUrls: readonly string[],
): string[] {
  const known = new Set(knownPageUrls);

  return uniq(favoriteUrls).filter(url => known.has(url));
}

/**
 * Moves an item one place up or down.
 *
 * @param items - The list; not modified.
 * @param index - What to move.
 * @param direction - Which way.
 * @returns The reordered list, or the same values when the move would fall off an end.
 */
export function moveItem<T>(items: readonly T[], index: number, direction: "up" | "down"): T[] {
  const target = direction === "up" ? index - 1 : index + 1;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) {
    return [...items];
  }

  const reordered = [...items];
  [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
  return reordered;
}

/**
 * Moves a page into a folder, at the end.
 *
 * @param folders - The current arrangement; not modified.
 * @param pageUrl - The page to move.
 * @param targetFolderId - Where it should end up.
 */
export function movePageToFolder(
  folders: readonly HomeFolder[],
  pageUrl: string,
  targetFolderId: string,
): HomeFolder[] {
  return folders.map(folder => {
    const without = folder.pageUrls.filter(url => url !== pageUrl);
    return {
      ...folder,
      pageUrls: folder.id === targetFolderId ? [...without, pageUrl] : without,
    };
  });
}

/**
 * Removes a folder, keeping its pages.
 *
 * The pages go to the folder before it, or the one after it when it was the first — deleting a
 * folder is meant to tidy the home page, not to hide pages.
 *
 * @param folders - The current arrangement; not modified.
 * @param folderId - The folder to remove.
 */
export function removeFolder(folders: readonly HomeFolder[], folderId: string): HomeFolder[] {
  const index = folders.findIndex(folder => folder.id === folderId);
  if (index < 0 || folders.length <= 1) {
    return [...folders];
  }

  const orphaned = folders[index].pageUrls;
  const remaining = folders.filter(folder => folder.id !== folderId);
  const adoptIndex = index === 0 ? 0 : index - 1;

  return remaining.map((folder, position) =>
    position === adoptIndex ? { ...folder, pageUrls: [...folder.pageUrls, ...orphaned] } : folder,
  );
}

/**
 * Puts back the pages the reader cannot currently see.
 *
 * What is drawn is the arrangement filtered down to the pages this reader may open, and an edit
 * is made against what is drawn. Stored as it is, that edit would delete every page that was
 * filtered out — a page whose licence check failed once, or that a colleague on another database
 * cannot open, would be filed out of the reader's folders for good.
 *
 * The hidden pages go back into the folder they were in, at the end.
 *
 * @param edited - The arrangement as the reader just changed it, over the visible pages.
 * @param stored - The arrangement as it is stored, including pages not currently visible.
 * @param knownPageUrls - The pages this reader may open.
 */
export function keepHiddenPages(
  edited: readonly HomeFolder[],
  stored: readonly HomeFolder[],
  knownPageUrls: readonly string[],
): HomeFolder[] {
  const known = new Set(knownPageUrls);
  const hiddenByFolderId = new Map<string, string[]>();

  for (const folder of stored) {
    if (!folder?.id) {
      continue;
    }
    const hidden = (folder.pageUrls ?? []).filter(url => !known.has(url));
    if (hidden.length > 0) {
      hiddenByFolderId.set(folder.id, hidden);
    }
  }

  if (hiddenByFolderId.size === 0) {
    return [...edited];
  }

  const withHidden = edited.map(folder => {
    const hidden = hiddenByFolderId.get(folder.id);
    hiddenByFolderId.delete(folder.id);
    return hidden ? { ...folder, pageUrls: [...folder.pageUrls, ...hidden] } : folder;
  });

  // A folder the reader deleted took its visible pages with it, which is what they asked for. Its
  // hidden ones would otherwise disappear silently, so they move to the first folder.
  const orphaned = [...hiddenByFolderId.values()].flat();
  if (orphaned.length > 0 && withHidden.length > 0) {
    withHidden[0] = {
      ...withHidden[0],
      pageUrls: [...withHidden[0].pageUrls, ...orphaned],
    };
  }

  return withHidden;
}

/**
 * Moves a favourite within the order the reader sees.
 *
 * Favourites that are not currently visible keep their place at the end rather than being lost:
 * moved by their position in the stored list, pressing "up" on the first visible entry swapped it
 * with something invisible and nothing appeared to happen.
 *
 * @param stored - The stored favourites, which may include pages this reader cannot open.
 * @param knownPageUrls - The pages this reader may open.
 */
export function moveFavoriteAmongVisible(
  stored: readonly string[],
  knownPageUrls: readonly string[],
  pageUrl: string,
  direction: "up" | "down",
): string[] {
  const visible = reconcileFavorites(stored, knownPageUrls);
  const index = visible.indexOf(pageUrl);
  if (index < 0) {
    return [...stored];
  }

  const hidden = stored.filter(url => !visible.includes(url));
  return [...moveItem(visible, index, direction), ...hidden];
}
